import React, { useEffect, useState } from 'react';
import Header from '../components/Layout/Header';
import { billingTokenService } from '../services/api';
import { TokenUsageSummary, TokenUsageHistory } from '../types';
import { DollarSign, TrendingUp, Activity, Clock, RefreshCw, Zap, Users, TestTube } from 'lucide-react';
import toast from 'react-hot-toast';

const BillingPage: React.FC = () => {
  const [usage, setUsage] = useState<TokenUsageSummary | null>(null);
  const [history, setHistory] = useState<TokenUsageHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [usageData, historyData] = await Promise.all([
        billingTokenService.getUsageSummary(),
        billingTokenService.getHistory(20),
      ]);
      setUsage(usageData);
      setHistory(historyData);
    } catch (error) {
      toast.error('Failed to load billing data');
      console.error('Billing load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBillingData();
    setRefreshing(false);
    toast.success('Billing data refreshed');
  };

  const formatTokens = (value: number | null) => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen">
        <Header title="Billing & Token Usage" subtitle="Monitor your token consumption and costs" />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const totalTokensAvailable = usage?.tokensRemaining !== null
    ? (usage?.tokensRemaining || 0) + (usage?.totalTokensUsed || 0)
    : null;

  const usagePercentage = totalTokensAvailable
    ? ((usage?.totalTokensUsed || 0) / totalTokensAvailable) * 100
    : 0;

  return (
    <div className="flex flex-col h-screen">
      <Header title="Billing & Token Usage" subtitle="Monitor your token consumption and costs from external API" />

      <div className="flex-1 overflow-y-auto p-8">
        {/* Refresh Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Token Balance Card */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium opacity-90">Token Balance</h3>
              <p className="text-sm opacity-70">Live from CodeJustin API (auto-synced)</p>
            </div>
            <Zap size={32} className="opacity-80" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm opacity-70 mb-1">Total Available</div>
              <div className="text-3xl font-bold">
                {totalTokensAvailable !== null ? formatTokens(totalTokensAvailable) : '—'}
              </div>
            </div>
            <div>
              <div className="text-sm opacity-70 mb-1">Used</div>
              <div className="text-3xl font-bold">{formatTokens(usage?.totalTokensUsed || 0)}</div>
            </div>
            <div>
              <div className="text-sm opacity-70 mb-1">Remaining</div>
              <div className="text-3xl font-bold">{formatTokens(usage?.tokensRemaining ?? null)}</div>
            </div>
          </div>

          {/* Progress Bar */}
          {totalTokensAvailable !== null && (
            <div className="mt-6">
              <div className="flex justify-between text-sm opacity-70 mb-2">
                <span>Usage</span>
                <span>{usagePercentage.toFixed(2)}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div
                  className="bg-white rounded-full h-3 transition-all duration-500"
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Activity size={20} className="text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Total Queries</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{usage?.totalQueries || 0}</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <TestTube size={20} className="text-orange-600" />
              </div>
              <span className="text-sm text-gray-500">Trial Queries</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{usage?.trial.queries || 0}</div>
            <div className="text-xs text-gray-400 mt-1">Cost: {formatCurrency(usage?.trial.costINR || 0)}</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Users size={20} className="text-green-600" />
              </div>
              <span className="text-sm text-gray-500">End-User Queries</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{usage?.endUser.queries || 0}</div>
            <div className="text-xs text-gray-400 mt-1">Revenue: {formatCurrency(usage?.endUser.revenueINR || 0)}</div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-purple-600" />
              </div>
              <span className="text-sm text-gray-500">Net Profit</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(usage?.profit.netProfitINR || 0)}</div>
            <div className="text-xs text-gray-400 mt-1">Gross: {formatCurrency(usage?.profit.grossRevenueINR || 0)}</div>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Trial Usage</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Tokens Used</span>
                <span className="font-medium">{formatTokens(usage?.trial.tokensUsed || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Rate per Token</span>
                <span className="font-medium">₹{usage?.trial.ratePerToken || 20}/token</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Cost</span>
                <span className="font-semibold text-red-600">{formatCurrency(usage?.trial.costINR || 0)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">End-User Revenue</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Tokens Used</span>
                <span className="font-medium">{formatTokens(usage?.endUser.tokensUsed || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Rate per Token</span>
                <span className="font-medium">₹{usage?.endUser.ratePerToken || 50}/token</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-semibold text-green-600">{formatCurrency(usage?.endUser.revenueINR || 0)}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Profit Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">End-User Revenue</span>
                <span className="font-medium text-green-600">{formatCurrency(usage?.profit.grossRevenueINR || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">End-User Cost</span>
                <span className="font-medium text-red-600">-{formatCurrency(usage?.profit.totalCostINR || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Trial Cost</span>
                <span className="font-medium text-orange-600">-{formatCurrency(usage?.profit.trialCostINR || 0)}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="text-gray-900 font-semibold">Net Profit</span>
                <span className={`font-bold ${(usage?.profit.netProfitINR || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(usage?.profit.netProfitINR || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Usage History */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
            <Clock size={18} className="text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-700">Recent Usage History</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Charged</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-400">
                      No usage history yet
                    </td>
                  </tr>
                ) : (
                  history.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-gray-600">{formatDate(entry.timestamp)}</td>
                      <td className="px-6 py-3 text-gray-900 font-medium">{entry.userId}</td>
                      <td className="px-6 py-3 text-gray-600">{entry.agentId}</td>
                      <td className="px-6 py-3">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{entry.endpoint}</span>
                      </td>
                      <td className="px-6 py-3 text-right font-medium text-red-600">
                        -{entry.tokensCharged.toFixed(4)}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-600">
                        {formatTokens(entry.tokensRemaining)}
                      </td>
                      <td className="px-6 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          entry.isTrial
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {entry.isTrial ? 'Trial' : 'Paid'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Last Updated */}
        {usage?.lastUpdated && (
          <div className="mt-4 text-center text-xs text-gray-400">
            Last updated: {new Date(usage.lastUpdated).toLocaleString('en-IN')}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
